/**
 * Per-case chat unread state (agent-bus P6 item 3,
 * `lexide-agent-bus-p6-spa-miniplan.md`).
 *
 * ⚖ D-8 makes turns land in threads nobody is watching: a mission finishes,
 * the concierge narrates the outcome into the CASE thread up to ~65 s later —
 * by which time the lawyer may be in another case. The gateway socket already
 * delivers those finals to this client (one socket, many sessions;
 * `useChatSession` filters by its own sessionKey and DEV-tallies the rest),
 * so the signal exists and was being discarded. `useChatUnreadTracker` routes
 * it here instead.
 *
 * Design decisions (this lane's to make, per the P6 handshake):
 * - Keyed by CASE id, not sessionKey — the affordance renders on case rows
 *   (OverviewNav, CommandPalette), and both the caseId-only and the legacy
 *   4-level session keys parse to a caseId (`parseLxSessionKey`).
 * - Counts FINALS, not deltas: one turn = one unread, however many frames
 *   streamed it.
 * - Session-lifetime only, deliberately NOT persisted: after a reload we
 *   cannot know what arrived while the app was closed without diffing
 *   history, and a persisted stale count would CLAIM that knowledge. An
 *   honest v1 tracks only what this client actually observed.
 * - Clearing is VIEW-driven: activating the case's chat session clears it.
 */
import { create } from "zustand";

interface ChatUnreadState {
  /** caseId → count of assistant finals observed while the case was not the
   *  active chat session. */
  unreadByCase: Record<string, number>;
  /** The sessionKey whose chat surface is currently mounted (null = none).
   *  Written by `useChatSession` on mount/unmount; read by the tracker to
   *  decide whether an arriving final is "background". */
  activeSessionKey: string | null;
  setActiveSessionKey: (sessionKey: string | null) => void;
  noteArrival: (caseId: string) => void;
  clearCase: (caseId: string) => void;
}

export const useChatUnreadStore = create<ChatUnreadState>((set) => ({
  unreadByCase: {},
  activeSessionKey: null,

  setActiveSessionKey: (sessionKey) => set({ activeSessionKey: sessionKey }),

  noteArrival: (caseId) =>
    set((s) => ({
      unreadByCase: {
        ...s.unreadByCase,
        [caseId]: (s.unreadByCase[caseId] ?? 0) + 1,
      },
    })),

  clearCase: (caseId) =>
    set((s) => {
      if (!(caseId in s.unreadByCase)) return s; // no churn
      const next = { ...s.unreadByCase };
      delete next[caseId];
      return { unreadByCase: next };
    }),
}));
